import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Star, Users, Landmark, Award, Calendar, Sparkles, HeartHandshake, Facebook, MessageSquare, Link2, Send, MapPin } from 'lucide-react';
import { Button } from '../ui/button';
import { Card, CardContent } from '../ui/card';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { MapLink } from '../ui/MapLink';
import { toast } from 'sonner';
import api, { formatApiErrorDetail } from '../../lib/api';
import { useContent } from '../../context/ContentContext';

const DOGWOOD_IMG = 'https://static.prod-images.emergentagent.com/jobs/fc3829d4-353f-4fc4-b817-cd1ab3ff1600/images/28fe3bdc0323843e75c503b53a19bd2f1fc25aae892bbc3c5d825302746702d3.jpeg';

const KICKOFF_DATE = new Date('2026-10-08T18:00:00-04:00'); // Next monthly meeting, 6:00 PM ET

const getTimeLeft = () => {
  const diff = KICKOFF_DATE - new Date();
  if (diff <= 0) return null;
  return {
    days: Math.floor(diff / (1000 * 60 * 60 * 24)),
    hours: Math.floor((diff / (1000 * 60 * 60)) % 24),
    minutes: Math.floor((diff / (1000 * 60)) % 60),
    seconds: Math.floor((diff / 1000) % 60),
  };
};

const benefits = [
  {
    icon: Star,
    title: 'Founding Member Designation',
    description: 'Be permanently recognized as one of the 25 women who charter the Women\'s Club of Lincoln County.',
  },
  {
    icon: Sparkles,
    title: 'Shape Our Programs',
    description: 'Founding members set the direction for our five GFWC-aligned program areas — from leadership to literacy.',
  },
  {
    icon: Users,
    title: 'Leadership Opportunities',
    description: 'Six founding board positions are open. Founding members have first opportunity to step into these roles.',
  },
  {
    icon: Landmark,
    title: 'A Voice in Our GFWC Path',
    description: 'Help guide our preparation to join the General Federation of Women\'s Clubs after our development period.',
  },
  {
    icon: HeartHandshake,
    title: 'Build the Community',
    description: 'Establish the traditions, partnerships, and service projects that will define the club for generations.',
  },
  {
    icon: Award,
    title: 'Charter-Year Experiences',
    description: 'Attend every first — the first meetings, the first service day, the first celebration — as a founder.',
  },
];

const CountdownUnit = ({ value, label }) => (
  <div className="bg-white rounded-2xl shadow-lg px-5 py-6 sm:px-8 sm:py-8 text-center min-w-[80px] sm:min-w-[110px]">
    <p className="font-serif text-4xl sm:text-5xl text-[#5d6b3c] tabular-nums" data-testid={`countdown-${label.toLowerCase()}`}>
      {String(value).padStart(2, '0')}
    </p>
    <p className="text-xs sm:text-sm text-[#3c4a2a]/60 uppercase tracking-wider mt-2">{label}</p>
  </div>
);

const SHARE_TEXT = "I'm helping found The Women's Club of Lincoln County — 25 founding members wanted for Fall 2026! Join me:";

const SHARE_CARD_IMG = '/share-card-qr.jpg';

const ShareCard = () => {
  const [downloading, setDownloading] = useState(false);

  const downloadCard = async () => {
    setDownloading(true);
    try {
      const res = await fetch(SHARE_CARD_IMG);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'wclc-recruitment-kickoff.jpg';
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success('Share card downloaded — post it anywhere!');
    } catch {
      window.open(SHARE_CARD_IMG, '_blank', 'noopener,noreferrer');
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div className="max-w-xl mx-auto mb-10" data-testid="share-card-section">
      <div className="rounded-2xl overflow-hidden shadow-xl border border-[#bd8cae]/30">
        <img
          src={SHARE_CARD_IMG}
          alt="Recruitment Kick-Off invitation — Thursday, Sept 10 from 6:00 to 8:30 PM at the Lincoln County Library — scan the QR code to become a founding member"
          className="w-full h-auto"
          data-testid="share-card-image"
        />
      </div>
      <div className="text-center mt-5">
        <Button
          onClick={downloadCard}
          disabled={downloading}
          data-testid="share-card-download-btn"
          className="bg-[#bd8cae] hover:bg-[#a3688f] text-white px-8"
        >
          <Send className="w-4 h-4 mr-2" />
          {downloading ? 'Downloading…' : 'Download Share Card'}
        </Button>
        <p className="text-xs text-[#3c4a2a]/50 mt-2">
          Save it, then post to Facebook, text it to a friend, or print it for the bulletin board.
        </p>
      </div>
    </div>
  );
};

const ShareButtons = () => {
  const shareUrl = `${window.location.origin}/founding-members`;

  const shareFacebook = () => {
    window.open(
      `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`,
      '_blank',
      'noopener,noreferrer,width=600,height=500'
    );
  };

  const shareText = () => {
    window.location.href = `sms:?&body=${encodeURIComponent(`${SHARE_TEXT} ${shareUrl}`)}`;
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      toast.success('Link copied — paste it anywhere!');
    } catch {
      toast.error('Could not copy the link automatically');
    }
  };

  return (
    <div className="flex flex-col sm:flex-row justify-center gap-3" data-testid="share-buttons">
      <Button
        onClick={shareFacebook}
        data-testid="share-facebook-btn"
        className="bg-[#1877f2] hover:bg-[#0f5ec7] text-white px-6"
      >
        <Facebook className="w-4 h-4 mr-2" />
        Share on Facebook
      </Button>
      <Button
        onClick={shareText}
        data-testid="share-sms-btn"
        className="bg-[#5d6b3c] hover:bg-[#3c4a2a] text-white px-6"
      >
        <MessageSquare className="w-4 h-4 mr-2" />
        Share by Text
      </Button>
      <Button
        onClick={copyLink}
        variant="outline"
        data-testid="share-copy-btn"
        className="border-[#5d6b3c] text-[#5d6b3c] hover:bg-[#5d6b3c] hover:text-white px-6"
      >
        <Link2 className="w-4 h-4 mr-2" />
        Copy Link
      </Button>
    </div>
  );
};

const InterestForm = () => {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [message, setMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      const { data } = await api.post('/founding/interest', { name, email, message });
      toast.success(data.message);
      setDone(true);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (done) {
    return (
      <div className="text-center py-6" data-testid="interest-form-success">
        <Sparkles className="w-10 h-10 text-[#5d6b3c] mx-auto mb-4" />
        <h3 className="font-serif text-2xl text-[#3c4a2a] mb-2">You're on the list!</h3>
        <p className="text-[#3c4a2a]/70">
          We'll reach out with founding member details before the fall kickoff.
        </p>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4" data-testid="interest-form">
      <div className="grid sm:grid-cols-2 gap-4">
        <div>
          <Label htmlFor="interest-name" className="text-[#3c4a2a]">Name *</Label>
          <Input
            id="interest-name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
            data-testid="interest-name-input"
            className="mt-2 border-[#bd8cae]/50 focus:border-[#5d6b3c] bg-white"
            placeholder="Your name"
          />
        </div>
        <div>
          <Label htmlFor="interest-email" className="text-[#3c4a2a]">Email *</Label>
          <Input
            id="interest-email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            data-testid="interest-email-input"
            className="mt-2 border-[#bd8cae]/50 focus:border-[#5d6b3c] bg-white"
            placeholder="your@email.com"
          />
        </div>
      </div>
      <div>
        <Label htmlFor="interest-message" className="text-[#3c4a2a]">Anything you'd like us to know?</Label>
        <Textarea
          id="interest-message"
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          rows={3}
          data-testid="interest-message-input"
          className="mt-2 border-[#bd8cae]/50 focus:border-[#5d6b3c] bg-white"
          placeholder="Questions, interests, how you'd like to help… (optional)"
        />
      </div>
      <Button
        type="submit"
        disabled={isSubmitting}
        data-testid="interest-submit-btn"
        className="w-full bg-[#5d6b3c] hover:bg-[#3c4a2a] text-white py-6 text-lg"
      >
        {isSubmitting ? 'Submitting…' : "Keep Me Posted"}
        <Send className="w-4 h-4 ml-2" />
      </Button>
      <p className="text-xs text-[#3c4a2a]/60 text-center">
        No commitment, no account needed — we'll simply keep you in the loop.
      </p>
    </form>
  );
};

const FoundingWall = () => {
  const [founders, setFounders] = useState([]);
  const backendBase = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    api.get('/founding-members').then((res) => setFounders(res.data)).catch(() => {});
  }, []);

  const byNumber = {};
  founders.forEach((f) => { if (f.number) byNumber[f.number] = f; });

  return (
    <section className="py-20 bg-white" data-testid="founding-wall">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <span className="text-[#bd8cae] text-sm uppercase tracking-wider font-medium">Honor Wall</span>
          <h2 className="font-serif text-3xl sm:text-4xl text-[#3c4a2a] mt-3 mb-4">Our Founding Members</h2>
          <p className="text-[#3c4a2a]/70 max-w-xl mx-auto">
            Celebrating the first 25 women writing the opening chapter of the Women's Club of Lincoln County.
          </p>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-4">
          {Array.from({ length: 25 }, (_, i) => i + 1).map((n) => {
            const f = byNumber[n];
            return (
              <div
                key={n}
                data-testid={`founding-slot-${n}`}
                className={`rounded-xl border p-4 text-center transition-all ${
                  f ? 'bg-[#f1e3eb]/70 border-[#bd8cae]/40 shadow-md' : 'bg-white border-dashed border-[#bd8cae]/40'
                }`}
              >
                <div className={`w-16 h-16 rounded-full mx-auto mb-3 overflow-hidden flex items-center justify-center ${f ? 'bg-[#5d6b3c] text-white' : 'bg-[#f1e3eb]'}`}>
                  {f?.photo_url ? (
                    <img src={`${backendBase}${f.photo_url}`} alt={f.name} className="w-full h-full object-cover" />
                  ) : f ? (
                    <span className="font-serif text-xl">{f.name.split(' ').map((w) => w[0]).slice(0, 2).join('')}</span>
                  ) : (
                    <Star className="w-6 h-6 text-[#bd8cae]" />
                  )}
                </div>
                <p className="text-xs uppercase tracking-wider text-[#a3688f] font-medium">No. {n}</p>
                {f ? (
                  <p className="font-serif text-[#3c4a2a] mt-1 leading-tight">{f.name}</p>
                ) : (
                  <p className="text-xs text-[#3c4a2a]/45 mt-1 italic">Waiting for you</p>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

const FoundingMembersPage = () => {
  const { c } = useContent();
  const [timeLeft, setTimeLeft] = useState(getTimeLeft());
  const [spots, setSpots] = useState(null);

  useEffect(() => {
    const timer = setInterval(() => setTimeLeft(getTimeLeft()), 1000);
    api.get('/founding/spots').then((res) => setSpots(res.data)).catch(() => {});
    return () => clearInterval(timer);
  }, []);

  return (
    <main className="pt-52" data-testid="founding-members-page">
      {/* Hero */}
      <section className="py-20 bg-[#f1e3eb]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
          <div className="max-w-3xl">
            <span className="text-[#bd8cae] text-sm uppercase tracking-wider font-medium">
              {c('founding.hero.eyebrow', 'Fall 2026 Recruitment')}
            </span>
            <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl text-[#3c4a2a] mt-3 mb-6">
              {c('founding.hero.title', 'Become a Founding Member')}
            </h1>
            <p className="text-lg text-[#3c4a2a]/80 leading-relaxed">
              {c('founding.hero.text', "We're looking for 25 founding members to charter the Women's Club of Lincoln County. This is a once-in-a-lifetime opportunity to build a women's organization from the ground up — its programs, its traditions, and its impact.")}
            </p>
            {spots && (
              <div className="mt-8 bg-white rounded-2xl shadow-md p-6 max-w-xl" data-testid="founding-spot-tracker">
                <div className="flex items-center justify-between mb-3">
                  <p className="font-medium text-[#3c4a2a]">
                    {spots.taken} of {spots.goal} founding spots claimed
                  </p>
                  <p className="text-sm font-semibold text-[#5d6b3c]" data-testid="founding-spots-remaining">
                    {spots.remaining} remaining
                  </p>
                </div>
                <div className="h-3 rounded-full bg-[#f1e3eb] overflow-hidden">
                  <div
                    className="h-full rounded-full bg-gradient-to-r from-[#5d6b3c] to-[#7c8a56] transition-all duration-700"
                    style={{ width: `${Math.min(100, (spots.taken / spots.goal) * 100)}%` }}
                  />
                </div>
                <p className="text-xs text-[#3c4a2a]/60 mt-3">
                  Spots are claimed as women create their member accounts — don't wait for fall to get started.
                </p>
              </div>
            )}
          </div>
          <div className="aspect-[3/2] rounded-2xl overflow-hidden shadow-xl" data-testid="founding-hero-image">
            <img
              src={c('founding.hero.image', DOGWOOD_IMG)}
              alt="Dogwood tree in full bloom"
              className="w-full h-full object-cover"
            />
          </div>
          </div>
        </div>
      </section>

      {/* Countdown */}
      <section className="py-20 bg-[#5d6b3c]">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <span className="text-[#f1e3eb]/80 text-sm uppercase tracking-wider font-medium">
            Mark Your Calendar
          </span>
          <h2 className="font-serif text-3xl sm:text-4xl text-white mt-3 mb-4">
            {c('founding.countdown.heading', 'Countdown to Our Next Meeting')}
          </h2>
          <p className="text-white/90 mb-3 max-w-2xl mx-auto">
            {c('founding.countdown.text', "Our next monthly membership meeting is Thursday, October 8, 2026 from 6:00 to 8:30 PM at the Charles R. Jonas Library in Lincolnton.")}
          </p>
          <p className="text-white/80 mb-10 flex items-center justify-center gap-1.5" data-testid="countdown-location">
            <MapPin className="w-4 h-4" />
            <MapLink location={c('site.meeting_location', 'Location TBD')} className="hover:text-white text-white/80" testId="countdown-map-link" />
          </p>
          {timeLeft ? (
            <div className="flex justify-center gap-3 sm:gap-6 flex-wrap" data-testid="kickoff-countdown">
              <CountdownUnit value={timeLeft.days} label="Days" />
              <CountdownUnit value={timeLeft.hours} label="Hours" />
              <CountdownUnit value={timeLeft.minutes} label="Minutes" />
              <CountdownUnit value={timeLeft.seconds} label="Seconds" />
            </div>
          ) : (
            <p className="font-serif text-3xl text-white" data-testid="kickoff-live">
              The fall recruitment cycle is underway — join us!
            </p>
          )}
          <div className="mt-10 flex flex-col sm:flex-row justify-center gap-4">
            <Link to="/events">
              <Button
                data-testid="founding-rsvp-btn"
                className="w-full sm:w-auto bg-[#f1e3eb] hover:bg-white text-[#3c4a2a] px-8 py-6 text-lg"
              >
                <Calendar className="w-5 h-5 mr-2" />
                RSVP to a Meeting
              </Button>
            </Link>
            <Link to="/register">
              <Button
                data-testid="founding-join-btn"
                className="w-full sm:w-auto bg-[#bd8cae] hover:bg-[#bd8cae]/90 text-[#3c4a2a] px-8 py-6 text-lg"
              >
                Start Your Membership
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-2xl mb-16">
            <span className="text-[#bd8cae] text-sm uppercase tracking-wider font-medium">
              Why Found, Not Just Join
            </span>
            <h2 className="font-serif text-3xl sm:text-4xl text-[#3c4a2a] mt-3 mb-4">
              {c('founding.benefits.heading', 'Founding Member Benefits')}
            </h2>
            <p className="text-[#3c4a2a]/70">
              {c('founding.benefits.text', "Founding members don't inherit a club — they create one. Here's what being one of the first 25 means.")}
            </p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-8">
            {benefits.map((benefit) => (
              <Card
                key={benefit.title}
                className="bg-[#f1e3eb] border-0 hover:shadow-lg transition-shadow"
              >
                <CardContent className="p-8">
                  <div className="w-14 h-14 rounded-full bg-[#bd8cae]/40 flex items-center justify-center mb-6">
                    <benefit.icon className="w-7 h-7 text-[#5d6b3c]" />
                  </div>
                  <h3 className="font-serif text-xl text-[#3c4a2a] mb-3">{benefit.title}</h3>
                  <p className="text-[#3c4a2a]/70 text-sm leading-relaxed">{benefit.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      <FoundingWall />

      {/* Interest Form + Share */}
      <section className="py-20 bg-[#e7cfdf]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-start">
            <div>
              <span className="text-[#a3688f] text-sm uppercase tracking-wider font-medium">
                Not Ready to Register?
              </span>
              <h2 className="font-serif text-3xl sm:text-4xl text-[#3c4a2a] mt-3 mb-4">
                Just Curious? Stay in the Loop.
              </h2>
              <p className="text-[#3c4a2a]/70 mb-8">
                Leave your name and email and we'll keep you posted on founding member 
                details, meeting reminders, and the fall kickoff — no account required.
              </p>
              <Card className="bg-[#f1e3eb] border-0 shadow-md">
                <CardContent className="p-6 sm:p-8">
                  <InterestForm />
                </CardContent>
              </Card>
            </div>
            <div className="lg:pt-24">
              <span className="text-[#a3688f] text-sm uppercase tracking-wider font-medium">
                Spread the Word
              </span>
              <h2 className="font-serif text-3xl sm:text-4xl text-[#3c4a2a] mt-3 mb-4">
                Know a Woman Who Should Be a Founder?
              </h2>
              <p className="text-[#3c4a2a]/70 mb-8">
                The best clubs are built by invitation. Share this page with a friend, 
                a sister, a coworker — anyone ready to help lead Lincoln County.
              </p>
              <ShareCard />
              <ShareButtons />
            </div>
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="py-20 bg-[#f1e3eb]">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-2xl mx-auto mb-16">
            <h2 className="font-serif text-3xl sm:text-4xl text-[#3c4a2a]">How to Become a Founder</h2>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {[
              { step: '1', title: 'Come to a Meeting', text: 'Attend a monthly membership meeting — RSVP on our Events page to see upcoming dates.' },
              { step: '2', title: 'Create Your Account', text: 'Register on our member portal and choose the membership level that fits you best.' },
              { step: '3', title: 'Help Us Build', text: 'Join a program area, consider a board role, and shape the club with your voice and vision.' },
            ].map((item) => (
              <div key={item.step} className="text-center">
                <div className="w-16 h-16 rounded-full bg-[#5d6b3c] text-white font-serif text-2xl flex items-center justify-center mx-auto mb-6">
                  {item.step}
                </div>
                <h3 className="font-serif text-xl text-[#3c4a2a] mb-3">{item.title}</h3>
                <p className="text-[#3c4a2a]/70 text-sm leading-relaxed">{item.text}</p>
              </div>
            ))}
          </div>
          <div className="text-center mt-14">
            <Link to="/membership">
              <Button className="bg-[#5d6b3c] hover:bg-[#3c4a2a] text-white px-8 py-6 text-lg" data-testid="founding-membership-btn">
                View Membership Levels
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* GFWC teaser */}
      <section className="py-16 bg-gradient-to-br from-[#bd8cae]/30 to-[#bd8cae]/10">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-serif text-2xl sm:text-3xl text-[#3c4a2a] mb-4">
            Part of Something Bigger
          </h2>
          <p className="text-[#3c4a2a]/80 mb-8">
            Our program goals align with the General Federation of Women's Clubs — and 
            founding members will help guide our path to joining after our initial 
            development period.
          </p>
          <Link to="/gfwc">
            <Button
              variant="outline"
              data-testid="founding-gfwc-link"
              className="border-[#5d6b3c] text-[#5d6b3c] hover:bg-[#5d6b3c] hover:text-white px-8"
            >
              Learn About GFWC
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </Link>
        </div>
      </section>
    </main>
  );
};

export default FoundingMembersPage;
